import React, {useEffect, useState} from 'react';
import {withTranslation} from 'react-i18next';

import getFullUserName from 'utils/getFullUserName';
import {parseHTMLMessage} from 'utils/parseHTMLMessage';

import StoreActions from '../../../actions/storeActions';
import StoreStore from '../../../store';

import Box from 'components/Box';
import Button from 'components/Button';
import Card from 'components/Card';
import Message from 'components/InboxMessage';
import Flex from 'components/Flex';
import Text from 'components/Text';
import PageTitle from 'components/PageTitle';
import PageBackLink from 'components/PageBackLink';
import PageSection from 'components/PageSection';
import {HOME_ROUTE} from '../../../routes';

const TIME_IN_DAY = 1000 * 60 * 60 * 24;

function datediff(date, basedate = new Date()) {
	try {
		date = new Date(date);
	} catch {
		return null;
	}
	return date.getTime() - basedate.getTime();
}

const MessageGroupTitle = ({children}) => (
	<Box py="s" pr="s">
		<Text as="h3" bold overflow="wrap" lineHeight={1.4} display="block">
			{children}
		</Text>
	</Box>
);

function Messages({t}) {
	const [messages, setMessages] = useState(StoreStore.getMessages());

	function setNewMessages() {
		setMessages(StoreStore.getMessages());
	}

	useEffect(() => {
		StoreActions.fetchMessages();

		StoreStore.addChangeMessagesListener(setNewMessages);

		return () => {
			StoreStore.removeChangeMessagesListener(setNewMessages);
		};
	}, []);

	const segmentedMessages = {
		today: [],
		yesterday: [],
		lastWeek: [],
		earlier: [],
	};

	const d = new Date();
	const MS_ELAPSED_TODAY =
		(d.getSeconds() + d.getMinutes() * 60 + d.getHours() * 60 * 60) * 1000;

	messages.forEach((message, index) => {
		const newMessage = (
			<Message
				key={index}
				id={message.id}
				isUnread={!message.read}
				time={message.created_time}
				sender={
					getFullUserName(message) || message.author || t('hub_title')
				}
				subject={message.name}
				onOpen={message.read ? undefined : StoreActions.readMessage}
			>
				{message.content && (
					<div
						dangerouslySetInnerHTML={{
							__html: parseHTMLMessage(message.content),
						}}
					/>
				)}
			</Message>
		);

		const diff = -datediff(message.created_time);
		if (diff < MS_ELAPSED_TODAY) {
			segmentedMessages.today.push(newMessage);
		} else if (diff < MS_ELAPSED_TODAY + TIME_IN_DAY) {
			segmentedMessages.yesterday.push(newMessage);
		} else if (diff < TIME_IN_DAY * 7) {
			segmentedMessages.lastWeek.push(newMessage);
		} else {
			segmentedMessages.earlier.push(newMessage);
		}
	});

	let todayPanelTitle;
	let yesterdayPanelTitle;
	let lastWeekPanelTitle;
	let earlierPanelTitle;

	if (segmentedMessages.today.length) {
		todayPanelTitle = (
			<MessageGroupTitle>{t('time_today')}</MessageGroupTitle>
		);
	}

	if (segmentedMessages.yesterday.length) {
		yesterdayPanelTitle = (
			<MessageGroupTitle>{t('time_yesterday')}</MessageGroupTitle>
		);
	}

	if (segmentedMessages.lastWeek.length) {
		lastWeekPanelTitle = (
			<MessageGroupTitle>{t('time_last_week')}</MessageGroupTitle>
		);
	}

	if (segmentedMessages.earlier.length) {
		earlierPanelTitle = (
			<MessageGroupTitle>{t('time_earlier')}</MessageGroupTitle>
		);
	}

	return (
		<PageSection>
			<Card p="m">
				<PageBackLink fallback={HOME_ROUTE} mb="xs" />
				<Flex mb="s">
					<Box grow shrink>
						<PageTitle size="large" id="inbox_heading">
							{t('navbar_inbox')}
						</PageTitle>
					</Box>
					<Box>
						<Button
							size="small"
							variant="transparent"
							icon="ok"
							onClick={() => StoreActions.readAllMessages()}
							ml="s"
						>
							{t('actions_mark_all_read')}
						</Button>
					</Box>
				</Flex>
				{todayPanelTitle}
				<div>{segmentedMessages.today}</div>
				{yesterdayPanelTitle}
				<div>{segmentedMessages.yesterday}</div>
				{lastWeekPanelTitle}
				<div>{segmentedMessages.lastWeek}</div>
				{earlierPanelTitle}
				<div>{segmentedMessages.earlier}</div>
			</Card>
		</PageSection>
	);
}

export default withTranslation()(Messages);
